import { useState } from 'react';
import type { AppSnapshot } from '../app/state';
import { callAction } from '../app/actions';
import { coveredByGroup } from '../lib/responsibility';
import { SubjectPicker } from './SubjectPicker';
import type { DocId } from '../lib/types';

/**
 * The editable DOC-responsibles picker — the shared SubjectPicker over the DOC eligibility
 * flags, saving a full-replace `PUT /api/docs/:id/responsibles` on every change (an immediate
 * act, no Save button). Lifted out of the doc bar's ownership cluster (V0.44) because the
 * dashboard's Documents tab assigns the same thing for the same language: ONE implementation,
 * two hosts, or the two would be one eligibility rule apart within a release.
 *
 * The two seed rules travel with it, for the same reasons the node picker has them:
 * group-expanded members are filtered out of the individual chips (`coveredByGroup`), so
 * removing the group later does not leave them behind as accidental individual rows; and the
 * CALLER renders this only once both directories have loaded — over an empty directory the
 * filter covers nobody and every member would land in the draft as an individual.
 *
 * Reseed by KEY when the page or the language changes (`key={docId:locale}` at the call site):
 * a different (doc, locale) resolves different assignments, and the drafts must not carry the
 * old ones.
 */
export function DocResponsiblesPicker({
  id = 'wf-responsibles',
  docId,
  locale,
  s,
  seedUsers,
  seedGroups,
  onChange,
  autoOpen = true,
}: {
  /** The wrapper's DOM id — `wf-responsibles` in the doc bar, another in the dashboard. */
  id?: string;
  docId: DocId;
  locale: string;
  s: AppSnapshot;
  seedUsers: readonly string[];
  seedGroups: readonly string[];
  /** The optimistic echo: what the host shows the moment a chip is added or removed. */
  onChange: (users: string[], groups: string[]) => void;
  autoOpen?: boolean;
}) {
  const [groups, setGroups] = useState<string[]>(() => [...seedGroups]);
  const [users, setUsers] = useState<string[]>(() => {
    const covered = coveredByGroup(seedGroups, s.groupDirectory);
    return seedUsers.filter((uid) => !covered.has(uid));
  });

  const put = (nextUsers: string[], nextGroups: string[]) => {
    onChange(nextUsers, nextGroups);
    void callAction('setDocResponsibles', docId, locale, nextUsers, nextGroups);
  };

  return (
    <SubjectPicker
      id={id}
      autoOpen={autoOpen}
      users={s.userDirectory}
      groups={s.groupDirectory}
      eligibleUser={(u) => u.canBeDocResponsible}
      eligibleGroup={(g) => g.canBeDocResponsible}
      chosenUsers={users}
      chosenGroups={groups}
      onChangeUsers={(next) => {
        setUsers(next);
        put(next, groups);
      }}
      onChangeGroups={(next) => {
        setGroups(next);
        put(users, next);
      }}
      emptyUsersText="Nobody is eligible — a responsible needs the DOC_RESPONSIBLE permission."
      emptyGroupsText="No group is eligible yet."
    />
  );
}
